import * as Str from 'effect/String';
import { FieldDeclaration, isFieldDeclaration } from './field-declaration';
import { FieldsetDeclaration } from './fieldset-declaration';
import {
  FormDeclarationNode,
  FormDeclarationTree,
  FormDeclarationTreeNode,
} from './form-declaration-tree';
import {
  FieldDefinition,
  FieldsetDefinition,
  PageDefinition,
} from './form.types';

/**
 * @param keys Set of keys.
 * @returns
 * A function that can be used as a predicate for the form declaration
 * to get all fields with certain keys.
 */
export function fieldsWithKeys(...keys: string[]) {
  return (value: FormDeclarationNode): value is FieldDeclaration =>
    isFieldDeclaration(value) && keys.includes(value.key);
}

export type FieldsetPath = readonly [string];
export type FieldPath = readonly [string, string];
export type Path = FieldsetPath | FieldPath | readonly string[];

/**
 * Data structure that contains the declaration of the form
 * from which a `DeclarativeForm` can be built.
 *
 * It offers utility functions for accessing fieldsets and fields.
 */
export class FormDeclaration {
  private readonly tree: FormDeclarationTree;

  constructor(public readonly pages: readonly PageDefinition[]) {
    this.tree = FormDeclarationTree.fromPages(pages);
  }

  public get root(): FormDeclarationTreeNode {
    return this.tree.root;
  }

  public get allFieldsets(): FieldsetDefinition[] {
    return this.pages.flatMap(({ fieldsets }) => fieldsets);
  }

  /**
   * @param path Path to the fieldset
   * @returns Fieldset at the given path or `undefined`.
   */
  public get(path: FieldsetPath): FieldsetDeclaration | undefined;
  /**
   * @param path Path to the field
   * @returns Field at the given path or `undefined`.
   */
  public get(path: FieldPath): FieldDeclaration | undefined;
  /**
   * @param path Path to the node
   * @returns Node at the given path or `undefined`.
   *
   * @example
   * // With path array
   * d.get(['fieldset-key', 'field-key']);
   *
   * @example
   * // With path string
   * d.get('fieldset-key.field-key');
   */
  public get(path: string | readonly string[]): FormDeclarationNode | undefined;
  /**
   * @param path Path to the field
   * @returns Field at the given path or `undefined`.
   */
  public get<TField extends FieldDefinition>(
    path: FieldPath,
  ): TField | undefined;
  /**
   * @param path Path to the fieldset
   * @returns Fieldset at the given path or `undefined`.
   */
  public get<TFieldset extends FieldsetDefinition>(
    path: FieldsetPath,
  ): TFieldset | undefined;

  public get(
    path: string | readonly string[],
  ): FormDeclarationNode | undefined {
    if (path === null) {
      return undefined;
    }

    // Transform path to an array
    if (Str.isString(path)) {
      path = path.split('.');
    }

    const result = this.tree.root.findBySegments(this.tree.root.key, ...path);
    return result?.value;
  }

  /**
   * @param predicate Typeguard function that is called with the value
   *
   * @returns
   * The value for which the `predicate` function returned `true`,
   * otherwise `undefined`.
   */
  public find<T extends FormDeclarationNode>(
    predicate: (n: FormDeclarationNode) => n is T,
  ): T;
  /**
   * @param predicate Function that is called with the value
   *
   * @returns
   * The value for which the `predicate` function returned `true`,
   * otherwise `undefined`.
   */
  public find(
    predicate: (n: FormDeclarationNode) => boolean,
  ): FormDeclarationNode | undefined;

  public find(
    predicate: (n: FormDeclarationNode) => boolean,
  ): FormDeclarationNode | undefined {
    return this.tree.root.queryNode((node) => predicate(node.value))?.value;
  }

  /**
   * @param key Key of the fieldset / field
   * @param predicate Type guard predicate
   *
   * @returns
   * The first fieldset / field that matches the
   * key and the given `predicate`
   */
  public findByKey<T extends FormDeclarationNode>(
    key: string,
    predicate: (n: FormDeclarationNode) => n is T,
  ): T | undefined;
  /**
   * @param key Key of the fieldset / field
   * @param predicate Predicate to query the result
   *
   * @returns
   * The first fieldset / field that matches the
   * key and the given `predicate`
   */
  public findByKey(
    key: string,
    predicate?: (n: FormDeclarationNode) => boolean,
  ): FormDeclarationNode | undefined;

  public findByKey(
    key: string,
    predicate: (n: FormDeclarationNode) => boolean = () => true,
  ): FormDeclarationNode | undefined {
    return this.find(
      (value: FormDeclarationNode) => value.key === key && predicate(value),
    );
  }

  /**
   * @param predicate Typeguard function that is called with the value
   *
   * @returns
   * All values for which the `predicate` function returned `true`.
   */
  public findAll<T extends FormDeclarationNode>(
    predicate: (n: FormDeclarationNode) => n is T,
  ): T[];
  /**
   * @param predicate Function that is called with the value
   *
   * @returns
   * All values for which the `predicate` function returned `true`.
   */
  public findAll(
    predicate: (n: FormDeclarationNode) => boolean,
  ): FormDeclarationNode[];
  public findAll(
    predicate: (n: FormDeclarationNode) => boolean,
  ): FormDeclarationNode[] {
    return this.root
      .queryAllNodes((node) => predicate(node.value))
      .map((node) => node.value);
  }

  /**
   * @returns
   * All paths to fields for which the `predicate`
   * function returned `true`.
   */
  public findAllPaths(
    predicate: (n: FormDeclarationNode) => n is FieldDeclaration,
  ): FieldPath[];
  /**
   * @returns
   * All paths to fieldsets for which the `predicate`
   * function returned `true`.
   */
  public findAllPaths(
    predicate: (n: FormDeclarationNode) => n is FieldsetDeclaration,
  ): FieldsetPath[];
  /**
   * @param predicate Function that is called with the value
   *
   * @returns
   * All paths for which the `predicate` function returned `true`.
   */
  public findAllPaths(predicate: (n: FormDeclarationNode) => boolean): Path[];
  public findAllPaths(predicate: (n: FormDeclarationNode) => boolean): Path[] {
    const nodePaths = this.root.queryAllPaths((node) => predicate(node.value));
    return nodePaths.map(([_root, ...nodePathSegments]) =>
      nodePathSegments.map((p) => p.value.key),
    );
  }
}
